import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, Search, Eye, PiggyBank, ArrowUpCircle, ArrowDownCircle, UserPlus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import GroupExtrasPanel from '../components/GroupExtrasPanel';
import { toast } from 'sonner';

const fmt = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const GroupsPage = ({ lookupMode = false }) => {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showCreateSavings, setShowCreateSavings] = useState(false);
  const [showGroupLoan, setShowGroupLoan] = useState(false);
  const [showShareInvest, setShowShareInvest] = useState(false);
  const [groupLoans, setGroupLoans] = useState([]);
  const [groupShares, setGroupShares] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [shareProducts, setShareProducts] = useState([]);
  const [creditScore, setCreditScore] = useState(null);
  const [showRepay, setShowRepay] = useState(null);
  const [repayForm, setRepayForm] = useState({ amount: '', member_id: '', from_group_savings: false });
  const [clients, setClients] = useState([]);
  const [savingsProducts, setSavingsProducts] = useState([]);
  const [search, setSearch] = useState('');

  const [form, setForm] = useState({ name: '', description: '', meeting_day: '', meeting_time: '', location: '', signatory_rule: 'any', signatories_required: 1 });
  const [memberForm, setMemberForm] = useState({ client_id: '', role: 'member', is_signatory: false });
  const [txForm, setTxForm] = useState({ amount: '', member_id: '', payment_type: 'Cash' });
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', signatories: [], payment_type: 'Cash' });
  const [savingsForm, setSavingsForm] = useState({ product_id: '' });
  const [loanForm, setLoanForm] = useState({ product_id: '', amount: '', term_months: 12, interest_rate: '' });
  const [shareInvestForm, setShareInvestForm] = useState({ product_id: '', shares_requested: '', fund_from_savings: true });

  const load = useCallback(async () => {
    if (lookupMode && !search) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = search ? `?q=${encodeURIComponent(search)}` : '';
      const [gRes, oRes] = await Promise.all([api.get(`/groups${params}`), api.get('/offices').catch(() => ({ data: [] }))]);
      setGroups(gRes.data);
      setOffices(oRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search, lookupMode]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { e.preventDefault(); load(); };

  const handleSubmit = async () => {
    try {
      await api.post('/groups', form);
      toast.success('Group created');
      setShowModal(false);
      setForm({ name: '', description: '', meeting_day: '', meeting_time: '', location: '', signatory_rule: 'any', signatories_required: 1 });
      load();
    } catch (e) { toast.error('Failed to create group'); }
  };

  const openDetail = async (groupId) => {
    try {
      const [res, loansRes, sharesRes, scoreRes] = await Promise.all([
        api.get(`/groups/${groupId}`),
        api.get(`/groups/${groupId}/loans`).catch(() => ({ data: [] })),
        api.get(`/groups/${groupId}/share-investments`).catch(() => ({ data: [] })),
        api.get(`/groups/${groupId}/credit-score`).catch(() => ({ data: null })),
      ]);
      setDetailData(res.data);
      setGroupLoans(loansRes.data || []);
      setGroupShares(sharesRes.data || []);
      setCreditScore(scoreRes.data);
      setShowDetail(groupId);
    } catch { toast.error('Failed to load group'); }
  };

  const refreshDetail = async () => { if (showDetail) openDetail(showDetail); };

  const handleAddMember = async () => {
    try {
      await api.post(`/groups/${showDetail}/members`, memberForm);
      toast.success('Member added');
      setShowAddMember(false);
      setMemberForm({ client_id: '', role: 'member', is_signatory: false });
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRemoveMember = async (clientId) => {
    try {
      await api.delete(`/groups/${showDetail}/members/${clientId}`);
      toast.success('Member removed');
      refreshDetail();
    } catch { toast.error('Failed'); }
  };

  const handleCreateSavings = async () => {
    try {
      await api.post(`/groups/${showDetail}/savings`, savingsForm);
      toast.success('Group savings account created');
      setShowCreateSavings(false);
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeposit = async () => {
    try {
      const res = await api.post(`/groups/${showDetail}/deposit`, { amount: parseFloat(txForm.amount), member_id: txForm.member_id, payment_type: txForm.payment_type });
      toast.success(res.data.message);
      setShowDeposit(false);
      setTxForm({ amount: '', member_id: '', payment_type: 'Cash' });
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleWithdraw = async () => {
    try {
      const res = await api.post(`/groups/${showDetail}/withdraw`, { amount: parseFloat(withdrawForm.amount), signatories: withdrawForm.signatories, payment_type: withdrawForm.payment_type });
      toast.success(res.data.message);
      setShowWithdraw(false);
      setWithdrawForm({ amount: '', signatories: [], payment_type: 'Cash' });
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const loadClients = async () => {
    if (!clients.length) { try { const r = await api.get('/clients?limit=200&status=active'); setClients(r.data.clients || r.data || []); } catch {} }
  };

  const loadSavingsProducts = async () => {
    if (!savingsProducts.length) { try { const r = await api.get('/savings-products'); setSavingsProducts(r.data || []); } catch {} }
  };

  const handleGroupLoan = async () => {
    try {
      const res = await api.post(`/groups/${showDetail}/loan`, loanForm);
      toast.success(`Group loan ${res.data.loan_number} created — EMI: R${res.data.emi}`);
      setShowGroupLoan(false);
      setLoanForm({ product_id: '', amount: '', term_months: 12, interest_rate: '' });
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleShareInvest = async () => {
    try {
      const res = await api.post(`/groups/${showDetail}/share-investment`, shareInvestForm);
      toast.success(`${res.data.shares} shares purchased for ${fmt(res.data.total_cost)}`);
      setShowShareInvest(false);
      setShareInvestForm({ product_id: '', shares_requested: '', fund_from_savings: true });
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const loadLoanProducts = async () => {
    if (!loanProducts.length) { try { const r = await api.get('/loan-products'); setLoanProducts(r.data || []); } catch {} }
  };
  const loadShareProducts = async () => {
    if (!shareProducts.length) { try { const r = await api.get('/share-products'); setShareProducts(r.data || []); } catch {} }
  };

  const handleDisburse = async (loanId) => {
    try {
      const res = await api.post(`/groups/${showDetail}/loan/${loanId}/disburse`, { disburse_to: 'group_savings' });
      toast.success(res.data.message + (res.data.fees > 0 ? ` (Fees: R${res.data.fees})` : ''));
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRepay = async () => {
    if (!showRepay) return;
    try {
      const res = await api.post(`/groups/${showDetail}/loan/${showRepay.id}/repay`, {
        amount: parseFloat(repayForm.amount),
        member_id: repayForm.member_id || undefined,
        from_group_savings: repayForm.from_group_savings,
      });
      toast.success(res.data.message + (res.data.fees > 0 ? ` (Fees: R${res.data.fees})` : ''));
      setShowRepay(null);
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="groups-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Clients</span><span>/</span><span className="text-[#1f1f1f] font-medium">Groups</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">GROUP OPERATIONS</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Groups</h1>
          {lookupMode && <p className="text-sm text-gray-500 mt-1">Search by group name or number</p>}
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-group-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Group
        </Button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by group name or number..." className="pl-9 text-sm" data-testid="group-search" />
        </div>
        <Button type="submit" className="bg-[#1f1f1f] text-white text-sm px-6">Search</Button>
      </form>

      {/* Table — hidden in lookupMode until search is performed */}
      {lookupMode && !search && groups.length === 0 ? (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Search for a group by name or number to view details</p>
        </div>
      ) : (
      <div className="bg-white border border-gray-200">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Group</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Number</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Members</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Meeting</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Signatory</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            groups.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">{search ? 'No groups match your search' : 'No groups found'}</td></tr> :
            groups.map(g => (
              <tr key={g.id} className="border-b hover:bg-gray-50" data-testid={`group-row-${g.id}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{g.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{g.group_number || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{g.member_count || 0}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{g.meeting_day ? `${g.meeting_day} ${g.meeting_time || ''}` : '-'}</td>
                <td className="px-4 py-3 text-xs text-gray-500 capitalize">{g.signatory_rule || 'any'}{g.signatory_rule === 'n_of_m' ? ` (${g.signatories_required})` : ''}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700">{g.status || 'Active'}</span></td>
                <td className="px-4 py-3">
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openDetail(g.id)} data-testid={`group-detail-${g.id}`}><Eye className="w-3 h-3 mr-1" />View</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Create Group Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="group-form-desc">
          <DialogHeader><DialogTitle>Create Group</DialogTitle></DialogHeader>
          <p id="group-form-desc" className="sr-only">Form to create a new group</p>
          <div className="space-y-4">
            <div><Label>Group Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="group-name" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Meeting Day</Label>
                <Select value={form.meeting_day} onValueChange={v => setForm({...form, meeting_day: v})}>
                  <SelectTrigger><SelectValue placeholder="Select Day" /></SelectTrigger>
                  <SelectContent>{['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Meeting Time</Label><Input type="time" value={form.meeting_time} onChange={e => setForm({...form, meeting_time: e.target.value})} /></div>
            </div>
            <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Withdrawal Signatory Rule</Label>
                <Select value={form.signatory_rule} onValueChange={v => setForm({...form, signatory_rule: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Member</SelectItem>
                    <SelectItem value="n_of_m">N-of-M Signatories</SelectItem>
                    <SelectItem value="all">All Signatories</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.signatory_rule === 'n_of_m' && (
                <div><Label>Signatories Required</Label><Input type="number" min="1" value={form.signatories_required} onChange={e => setForm({...form, signatories_required: e.target.value})} /></div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="group-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby="group-detail-desc">
          <DialogHeader><DialogTitle>{detailData?.name} — {detailData?.group_number}</DialogTitle></DialogHeader>
          <p id="group-detail-desc" className="sr-only">Group details</p>
          {detailData && (
            <div className="space-y-5">
              {/* Info */}
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-gray-500">Meeting:</span> {detailData.meeting_day || '-'} {detailData.meeting_time || ''}</div>
                <div><span className="text-gray-500">Location:</span> {detailData.location || '-'}</div>
                <div><span className="text-gray-500">Signatory:</span> <span className="capitalize">{detailData.signatory_rule || 'any'}</span></div>
              </div>

              {/* Members */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Members ({detailData.members?.length || 0})</h4>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { setShowAddMember(true); loadClients(); }} data-testid="add-member-btn"><UserPlus className="w-3 h-3 mr-1" />Add Member</Button>
                </div>
                <div className="border border-gray-200">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-gray-50 border-b"><th className="text-left px-3 py-2">Client</th><th className="text-left px-3 py-2">Role</th><th className="text-left px-3 py-2">Signatory</th><th className="px-3 py-2"></th></tr></thead>
                    <tbody>
                      {(detailData.members || []).map((m, i) => (
                        <tr key={i} className="border-b">
                          <td className="px-3 py-2 font-medium">{m.client_name} <span className="text-gray-400">({m.client_number})</span></td>
                          <td className="px-3 py-2 capitalize">{m.role}</td>
                          <td className="px-3 py-2">{m.is_signatory ? <span className="text-green-600">Yes</span> : 'No'}</td>
                          <td className="px-3 py-2 text-right"><button onClick={() => handleRemoveMember(m.client_id)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button></td>
                        </tr>
                      ))}
                      {(!detailData.members || detailData.members.length === 0) && <tr><td colSpan={4} className="text-center py-3 text-gray-400">No members yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Group Savings Account */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Group Savings</h4>
                {detailData.savings_account ? (
                  <div className="p-4 bg-emerald-50 border border-emerald-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-mono text-emerald-800">{detailData.savings_account.account_number}</p>
                        <p className="text-xs text-emerald-600">Balance: <span className="font-bold text-lg">{fmt(detailData.savings_account.balance)}</span></p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-emerald-600 text-white text-xs h-7" onClick={() => setShowDeposit(true)} data-testid="group-deposit-btn"><ArrowUpCircle className="w-3 h-3 mr-1" />Deposit</Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 border-emerald-300" onClick={() => setShowWithdraw(true)} data-testid="group-withdraw-btn"><ArrowDownCircle className="w-3 h-3 mr-1" />Withdraw</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 border border-gray-200 text-center">
                    <PiggyBank className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-500 mb-2">No group savings account</p>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowCreateSavings(true); loadSavingsProducts(); }} data-testid="create-group-savings-btn"><Plus className="w-3 h-3 mr-1" />Open Group Savings</Button>
                  </div>
                )}
              </div>

              {/* Group Credit Score */}
              {creditScore && (
                <div className={`p-3 border ${creditScore.rating === 'Excellent' ? 'bg-emerald-50 border-emerald-200' : creditScore.rating === 'Good' ? 'bg-blue-50 border-blue-200' : creditScore.rating === 'Fair' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Group Credit Score</h4>
                      <p className="text-2xl font-bold text-[#1f1f1f]">{creditScore.group_score}<span className="text-xs font-normal text-gray-500 ml-2">/ 100 — {creditScore.rating}</span></p>
                    </div>
                    <div className="text-right text-[10px] text-gray-500 space-y-0.5">
                      {Object.entries(creditScore.factors || {}).map(([k, v]) => (
                        <div key={k}>{v.weight} {k.replace(/_/g, ' ')}: <span className="font-mono">{v.component}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Group Loans */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Group Loans ({groupLoans.length})</h4>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { setShowGroupLoan(true); loadLoanProducts(); }} data-testid="group-loan-btn"><Plus className="w-3 h-3 mr-1" />Apply for Group Loan</Button>
                </div>
                {groupLoans.length > 0 ? (
                  <div className="border border-gray-200 max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-gray-50 border-b"><th className="text-left px-3 py-1.5">Loan #</th><th className="text-right px-3 py-1.5">Principal</th><th className="text-right px-3 py-1.5">Outstanding</th><th className="text-right px-3 py-1.5">EMI</th><th className="text-left px-3 py-1.5">Status</th><th className="px-3 py-1.5">Actions</th></tr></thead>
                      <tbody>{groupLoans.map(l => (<tr key={l.id} className="border-b">
                        <td className="px-3 py-1 font-mono">{l.loan_number}</td>
                        <td className="px-3 py-1 text-right">{fmt(l.principal)}</td>
                        <td className="px-3 py-1 text-right font-bold">{fmt(l.outstanding_balance)}</td>
                        <td className="px-3 py-1 text-right">{fmt(l.emi)}</td>
                        <td className="px-3 py-1 capitalize">{l.status}</td>
                        <td className="px-3 py-1">
                          {(l.status === 'pending' || l.status === 'approved') && <Button size="sm" className="h-5 px-2 text-[10px] bg-green-600 text-white" onClick={() => handleDisburse(l.id)}>Disburse</Button>}
                          {(l.status === 'disbursed' || l.status === 'active') && <Button size="sm" variant="outline" className="h-5 px-2 text-[10px]" onClick={() => { setShowRepay(l); setRepayForm({ amount: String(l.emi || ''), member_id: '', from_group_savings: false }); }}>Repay</Button>}
                        </td>
                      </tr>))}</tbody>
                    </table>
                  </div>
                ) : <p className="text-xs text-gray-400">No group loans</p>}
              </div>

              {/* Group Share Investments */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Share Investments ({groupShares.length})</h4>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { setShowShareInvest(true); loadShareProducts(); }} data-testid="group-share-invest-btn"><Plus className="w-3 h-3 mr-1" />Invest in Shares</Button>
                </div>
                {groupShares.length > 0 ? (
                  <div className="border border-gray-200 max-h-32 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-gray-50 border-b"><th className="text-left px-3 py-1.5">Product</th><th className="text-right px-3 py-1.5">Shares</th><th className="text-right px-3 py-1.5">Value</th><th className="text-right px-3 py-1.5">Dividends</th></tr></thead>
                      <tbody>{groupShares.map(s => (<tr key={s.id} className="border-b"><td className="px-3 py-1">{s.product_name}</td><td className="px-3 py-1 text-right font-mono">{s.shares}</td><td className="px-3 py-1 text-right">{fmt(s.total_value)}</td><td className="px-3 py-1 text-right text-emerald-600">{fmt(s.dividends_earned)}</td></tr>))}</tbody>
                    </table>
                  </div>
                ) : <p className="text-xs text-gray-400">No share investments</p>}
              </div>

              {/* NEW: Charter / Meetings / Chat */}
              <GroupExtrasPanel group={detailData} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="max-w-md" aria-describedby="add-member-desc">
          <DialogHeader><DialogTitle>Add Member</DialogTitle></DialogHeader>
          <p id="add-member-desc" className="sr-only">Add client to group</p>
          <div className="space-y-4">
            <div><Label>Client *</Label>
              <Select value={memberForm.client_id} onValueChange={v => setMemberForm({...memberForm, client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.client_number})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Role</Label>
                <Select value={memberForm.role} onValueChange={v => setMemberForm({...memberForm, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="chairperson">Chairperson</SelectItem>
                    <SelectItem value="secretary">Secretary</SelectItem>
                    <SelectItem value="treasurer">Treasurer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-1">
                <input type="checkbox" checked={memberForm.is_signatory} onChange={e => setMemberForm({...memberForm, is_signatory: e.target.checked})} id="is-sig" />
                <Label htmlFor="is-sig" className="text-sm">Is Signatory</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
            <Button onClick={handleAddMember} className="bg-[#1f1f1f] text-white">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Deposit Dialog */}
      <Dialog open={showDeposit} onOpenChange={setShowDeposit}>
        <DialogContent className="max-w-sm" aria-describedby="grp-deposit-desc">
          <DialogHeader><DialogTitle>Group Deposit</DialogTitle></DialogHeader>
          <p id="grp-deposit-desc" className="sr-only">Deposit into group savings</p>
          <div className="space-y-4">
            <div><Label>Amount (ZAR) *</Label><Input type="number" step="0.01" value={txForm.amount} onChange={e => setTxForm({...txForm, amount: e.target.value})} /></div>
            <div><Label>Depositing Member</Label>
              <Select value={txForm.member_id || 'none'} onValueChange={v => setTxForm({...txForm, member_id: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select member (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Group contribution —</SelectItem>
                  {(detailData?.members || []).map(m => <SelectItem key={m.client_id} value={m.client_id}>{m.client_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeposit(false)}>Cancel</Button>
            <Button onClick={handleDeposit} className="bg-emerald-600 text-white">Deposit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Withdraw Dialog */}
      <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <DialogContent className="max-w-md" aria-describedby="grp-withdraw-desc">
          <DialogHeader><DialogTitle>Group Withdrawal</DialogTitle></DialogHeader>
          <p id="grp-withdraw-desc" className="sr-only">Withdraw from group savings</p>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Rule: <span className="font-medium capitalize">{detailData?.signatory_rule || 'any'}</span>
              {detailData?.signatory_rule === 'n_of_m' && ` (${detailData.signatories_required} required)`}
              {detailData?.signatory_rule === 'all' && ' (all signatories must authorize)'}
            </p>
            <div><Label>Amount (ZAR) *</Label><Input type="number" step="0.01" value={withdrawForm.amount} onChange={e => setWithdrawForm({...withdrawForm, amount: e.target.value})} /></div>
            {detailData?.signatory_rule !== 'any' && (
              <div>
                <Label>Authorizing Signatories *</Label>
                <div className="space-y-1 mt-1">
                  {(detailData?.members || []).filter(m => m.is_signatory).map(m => (
                    <label key={m.client_id} className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={withdrawForm.signatories.includes(m.client_id)} onChange={e => {
                        setWithdrawForm(prev => ({ ...prev, signatories: e.target.checked ? [...prev.signatories, m.client_id] : prev.signatories.filter(s => s !== m.client_id) }));
                      }} />
                      {m.client_name} ({m.role})
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdraw(false)}>Cancel</Button>
            <Button onClick={handleWithdraw} className="bg-red-600 text-white">Withdraw</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Group Savings Dialog */}
      <Dialog open={showCreateSavings} onOpenChange={setShowCreateSavings}>
        <DialogContent className="max-w-sm" aria-describedby="grp-savings-desc">
          <DialogHeader><DialogTitle>Open Group Savings Account</DialogTitle></DialogHeader>
          <p id="grp-savings-desc" className="sr-only">Create group savings</p>
          <div className="space-y-4">
            <div><Label>Savings Product *</Label>
              <Select value={savingsForm.product_id} onValueChange={v => setSavingsForm({...savingsForm, product_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{savingsProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.interest_rate || 0}% p.a.)</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSavings(false)}>Cancel</Button>
            <Button onClick={handleCreateSavings} className="bg-[#1f1f1f] text-white">Open Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Loan Dialog */}
      <Dialog open={showGroupLoan} onOpenChange={setShowGroupLoan}>
        <DialogContent className="max-w-md" aria-describedby="grp-loan-desc">
          <DialogHeader><DialogTitle>Apply for Group Loan (Grameen)</DialogTitle></DialogHeader>
          <p id="grp-loan-desc" className="sr-only">Joint liability group loan</p>
          <div className="space-y-4">
            <div className="p-2 bg-blue-50 border border-blue-200 text-xs text-blue-800">Joint liability: All {detailData?.members?.length || 0} members are collectively responsible for repayment.</div>
            <div><Label>Loan Product</Label>
              <Select value={loanForm.product_id} onValueChange={v => { const p = loanProducts.find(lp => lp.id === v); setLoanForm({...loanForm, product_id: v, interest_rate: p?.interest_rate || ''}); }}>
                <SelectTrigger><SelectValue placeholder="Select loan product" /></SelectTrigger>
                <SelectContent>{loanProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.interest_rate}%)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Total Amount (ZAR) *</Label><Input type="number" value={loanForm.amount} onChange={e => setLoanForm({...loanForm, amount: e.target.value})} data-testid="group-loan-amount" /></div>
              <div><Label>Term (months) *</Label><Input type="number" value={loanForm.term_months} onChange={e => setLoanForm({...loanForm, term_months: e.target.value})} /></div>
            </div>
            {loanForm.amount && detailData?.members?.length > 0 && (
              <p className="text-xs text-gray-500">Per member (equal split): {fmt(parseFloat(loanForm.amount || 0) / detailData.members.length)}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupLoan(false)}>Cancel</Button>
            <Button onClick={handleGroupLoan} className="bg-[#1f1f1f] text-white" data-testid="group-loan-submit">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Share Investment Dialog */}
      <Dialog open={showShareInvest} onOpenChange={setShowShareInvest}>
        <DialogContent className="max-w-md" aria-describedby="grp-share-desc">
          <DialogHeader><DialogTitle>Group Share Investment</DialogTitle></DialogHeader>
          <p id="grp-share-desc" className="sr-only">Invest group funds in shares</p>
          <div className="space-y-4">
            <div><Label>Share Product *</Label>
              <Select value={shareInvestForm.product_id} onValueChange={v => setShareInvestForm({...shareInvestForm, product_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select share product" /></SelectTrigger>
                <SelectContent>{shareProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — R{p.unit_price || p.nominal_price || 0}/share</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Number of Shares *</Label><Input type="number" min="1" value={shareInvestForm.shares_requested} onChange={e => setShareInvestForm({...shareInvestForm, shares_requested: e.target.value})} data-testid="group-share-qty" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={shareInvestForm.fund_from_savings} onChange={e => setShareInvestForm({...shareInvestForm, fund_from_savings: e.target.checked})} id="fund-savings" />
              <Label htmlFor="fund-savings" className="text-sm">Fund from group savings account</Label>
            </div>
            {shareInvestForm.fund_from_savings && detailData?.savings_account && (
              <p className="text-xs text-gray-500">Available in savings: {fmt(detailData.savings_account.balance)}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareInvest(false)}>Cancel</Button>
            <Button onClick={handleShareInvest} className="bg-[#1f1f1f] text-white" data-testid="group-share-submit">Invest</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Loan Repayment Dialog */}
      <Dialog open={!!showRepay} onOpenChange={() => setShowRepay(null)}>
        <DialogContent className="max-w-md" aria-describedby="grp-repay-desc">
          <DialogHeader><DialogTitle>Repay Group Loan — {showRepay?.loan_number}</DialogTitle></DialogHeader>
          <p id="grp-repay-desc" className="sr-only">Group loan repayment</p>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Outstanding: <span className="font-bold">{fmt(showRepay?.outstanding_balance)}</span> | EMI: {fmt(showRepay?.emi)}</p>
            <div><Label>Amount (ZAR) *</Label><Input type="number" step="0.01" value={repayForm.amount} onChange={e => setRepayForm({...repayForm, amount: e.target.value})} data-testid="group-repay-amount" /></div>
            <div><Label>Paying Member</Label>
              <Select value={repayForm.member_id || 'none'} onValueChange={v => setRepayForm({...repayForm, member_id: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select member (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Joint payment —</SelectItem>
                  {(detailData?.members || []).map(m => <SelectItem key={m.client_id} value={m.client_id}>{m.client_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={repayForm.from_group_savings} onChange={e => setRepayForm({...repayForm, from_group_savings: e.target.checked})} id="repay-savings" />
              <Label htmlFor="repay-savings" className="text-sm">Pay from group savings</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepay(null)}>Cancel</Button>
            <Button onClick={handleRepay} className="bg-[#1f1f1f] text-white" data-testid="group-repay-submit">Repay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupsPage;
